import * as React from "react";
import { LockOpenIcon, InfoIcon } from "../../../components/icons";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Progress, Spinner } from "../../../components/ui/feedback";
import { Textarea } from "../../../components/ui/textarea";
import { useCheckInDocumentMutation, useSaveDraftContentMutation } from "../api/checkoutApi";
import { DocumentCheckOutAdapter, useHostAdapter } from "../../../platform";
import { useCheckOutVerification, verificationMessage } from "../hooks/useCheckOutVerification";
import { describeError } from "../../../utils/describeError";
import PaneStatus from "../../../components/feedback/PaneStatus";
import { FieldLabel } from "../../../components/forms/FormSection";

// The check-in form: a comment (optional, multi-line) and nothing else. No version
// label — the server assigns the number and shows it after the fact. It rides in a
// sheet over the pane, so backing out returns the user to exactly the draft and chat
// they were in.

export interface CheckInViewProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  documents: DocumentCheckOutAdapter;
  /** Closes the sheet. Cancel is immediate; success waits for acknowledgement. */
  onClose?: () => void;
}

/**
 * How long the success message stays up before the document closes: long enough to read that
 * the check in landed, short enough that nobody starts typing into the window first.
 */
export const CLOSE_AFTER_CHECK_IN_MS = 3000;

/** A quiet note, for something the user should know before pressing the button. Not an Alert: nothing is wrong. */
const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-2.5 rounded-2xl border border-border bg-grey-50 px-3 py-2.5">
    <InfoIcon className="mt-px size-[18px] flex-none text-muted-foreground" />
    <p className="text-body2 text-muted-foreground">{children}</p>
  </div>
);

const CheckInView = ({ documents, onClose }: CheckInViewProps) => {
  const verification = useCheckOutVerification(documents);
  const [saveDraft] = useSaveDraftContentMutation();
  const [checkIn] = useCheckInDocumentMutation();
  const [comment, setComment] = React.useState("");
  const [progress, setProgress] = React.useState<number | null>(null);
  const [phase, setPhase] = React.useState<"form" | "working" | "done" | "failed">("form");
  const [versionNumber, setVersionNumber] = React.useState<number | null>(null);
  const [failureDetail, setFailureDetail] = React.useState<string | null>(null);
  const { displayName } = useHostAdapter();
  // After a check in the window can no longer reach Jylo, yet it still looks editable -- so
  // the document is closed for the user, where the host allows it, a moment after the
  // success message appears. Decided once, when the check in lands.
  const [closing, setClosing] = React.useState<"idle" | "scheduled" | "failed">("idle");

  const closeDocument = React.useCallback(async () => {
    try {
      await documents.closer?.close();
    } catch {
      setClosing("failed");
    }
  }, [documents]);

  React.useEffect(() => {
    if (closing !== "scheduled") return undefined;
    const timer = window.setTimeout(() => void closeDocument(), CLOSE_AFTER_CHECK_IN_MS);
    return () => window.clearTimeout(timer);
  }, [closing, closeDocument]);

  const handleCheckIn = async () => {
    if (verification.status !== "ok" || !verification.identity) return;
    setPhase("working");
    setFailureDetail(null);
    try {
      // The live document is never mutated here: sessions opened via an Office URI
      // scheme (ms-word:ofe|u| and its Excel / PowerPoint equivalents) are READ-ONLY,
      // so deleting the identity part locally would throw. The API strips the part
      // from the uploaded bytes at check in instead.
      const content = await documents.getDocumentBytes((fraction) => setProgress(fraction));
      setProgress(null);

      await saveDraft({
        projectObjectIdentifier: verification.identity.projectObjectIdentifier,
        documentObjectIdentifier: verification.identity.documentObjectIdentifier,
        content,
        extension: documents.fileExtension,
      }).unwrap();

      const version = await checkIn({
        projectObjectIdentifier: verification.identity.projectObjectIdentifier,
        documentObjectIdentifier: verification.identity.documentObjectIdentifier,
        versionComment: comment.trim() || undefined,
      }).unwrap();

      // Nothing to do about the buttons: checking in invalidates the check-out states,
      // so the header's verification refetches, finds no open draft, and disables Save
      // and Check in with the reason. That used to need an explicit ribbon call --
      // Office.ribbon.requestUpdate -- because the document itself cannot be trusted to
      // say: check in deliberately leaves the <jyloCheckOut> part in the live file, so
      // the part stops being a truthful signal the moment we end the check out.

      setVersionNumber(version.versionNumber);
      setPhase("done");
      if (documents.closer?.isAvailable()) setClosing("scheduled");
    } catch (error) {
      setProgress(null);
      setFailureDetail(describeError(error));
      setPhase("failed");
    }
  };

  // Order matters. Checking in invalidates the check-out states, so the refetch that
  // lands a moment later correctly reports this document as no longer checked out —
  // but rendered INSTEAD of the success state it reads as "check in failed". A
  // finished check in owns the screen.
  if (phase === "done") {
    // While the document is about to close, the title and the closing line say everything:
    // the advice about further edits and the Done button are for a window that stays open.
    const closingSoon = closing === "scheduled";
    return (
      <PaneStatus
        compact
        icon={<LockOpenIcon className="size-7 text-success" />}
        title={`Checked in — version ${versionNumber} published`}
        description={
          closingSoon
            ? undefined
            : "Your check out has ended and this document is back in Jylo. Further edits in this window will not reach Jylo — check it out again to keep working on it."
        }
        action={
          <div className="flex flex-col items-center gap-4">
            {!closingSoon && (
              <Button size="lg" onClick={onClose}>
                Done
              </Button>
            )}
            {closingSoon && (
              <div role="status" aria-live="polite" className="flex flex-row items-center gap-2">
                <Spinner size={16} />
                <span className="text-body2 text-muted-foreground">Closing…</span>
              </div>
            )}
            {closing === "failed" && (
              <p className="text-center text-body2 text-muted-foreground">
                {displayName} could not close this document. Close it yourself — your check in is
                complete.
              </p>
            )}
          </div>
        }
      />
    );
  }

  // Verification gates the form only. "No longer checked out" is the right thing to
  // say to someone opening this on a stale or non-Jylo document — and the wrong thing
  // to say to someone who just successfully checked in, which is why it lives here.
  if (phase === "form") {
    if (verification.status === "loading") {
      return <PaneStatus compact loading title="Checking this document…" />;
    }

    if (verification.status !== "ok") {
      // A failed read is the one non-ok state worth retrying in place -- the host would
      // not answer, which is not a fact about the draft. Every other state IS a fact and
      // a retry cannot change it, so it gets no button. Same rule as SaveView.
      const retryable = verification.status === "read-failed";
      return (
        <div className="flex flex-col gap-3 px-4 pb-4">
          <Alert severity={verification.status === "no-identity" ? "info" : "warning"}>
            {verification.readFailure ??
              verificationMessage(verification.status, verification.state)}
          </Alert>
          {retryable && (
            <Button variant="outline" size="lg" onClick={verification.refresh}>
              Try again
            </Button>
          )}
        </div>
      );
    }
  }

  const documentName = verification.state?.documentName;
  const draftOf = verification.state?.draftVersionNumber;
  const working = phase === "working";

  return (
    <div className="flex flex-col gap-3.5 px-4 pb-4">
      <div className="min-w-0">
        <h2 className="text-h6 text-strong">Check in</h2>
        {documentName && (
          <div className="truncate text-caption text-muted-foreground">
            {documentName}
            {draftOf ? ` · draft of version ${draftOf}` : ""}
          </div>
        )}
      </div>

      <Note>
        Publishes a new version and ends your check out. To make further changes you will need to
        check the document out from Jylo again.
      </Note>

      <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor="check-in-comment">Comment (optional)</FieldLabel>
        {/* No ceiling, as MUI's multiline without maxRows had none: the field grows with
            the comment and the sheet scrolls. */}
        <Textarea
          id="check-in-comment"
          autoGrow={{ minRows: 3, maxRows: Infinity }}
          placeholder="What changed in this version?"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          disabled={working}
        />
      </div>

      {working && (progress !== null ? <Progress value={progress * 100} /> : <Progress />)}

      {phase === "failed" && (
        <Alert severity="error">
          Check in failed. The draft is still checked out to you — nothing was lost.
          {failureDetail && <div className="mt-1 text-caption break-words">{failureDetail}</div>}
        </Alert>
      )}

      <div className="flex flex-row justify-end gap-2">
        <Button variant="ghost" size="lg" onClick={onClose} disabled={working}>
          Cancel
        </Button>
        <Button size="lg" onClick={() => void handleCheckIn()} disabled={working}>
          <LockOpenIcon />
          Check in
        </Button>
      </div>
    </div>
  );
};

export default CheckInView;
